import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * A row of headline numbers in one bordered strip, divided by hairlines rather than
 * boxed one by one. `columns` is the count at the widest breakpoint; below it the
 * cells wrap two to a row, then one.
 */
export function StatStrip({
  columns = 4,
  className,
  children,
}: {
  columns?: 2 | 3 | 4 | 6;
  className?: string;
  children: ReactNode;
}) {
  // Hairlines between cells only: each cell draws its top and left edge, and the grid is
  // nudged a pixel up and left so the outer ones hide under the frame.
  return (
    <div className={cn("overflow-hidden rounded-xl border bg-card", className)}>
      <dl
        className={cn(
          "-mt-px -ml-px grid grid-cols-1 @md/main:grid-cols-2 [&>*]:border-t [&>*]:border-l",
          columns === 3 && "@3xl/main:grid-cols-3",
          columns === 4 && "@5xl/main:grid-cols-4",
          columns === 6 && "@3xl/main:grid-cols-3 @6xl/main:grid-cols-6",
        )}
      >
        {children}
      </dl>
    </div>
  );
}

export function StatCell({
  label,
  value,
  detail,
  icon: Icon,
  tone = "neutral",
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  icon?: LucideIcon;
  tone?: "neutral" | "good" | "bad" | "warn";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-1.5 px-4 py-3.5 text-left transition-colors duration-(--motion-dur) hover:bg-muted/40",
        className,
      )}
    >
      <dt className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {Icon && <Icon className="size-3.5 shrink-0" />}
        <span className="truncate">{label}</span>
      </dt>
      <dd
        className={cn(
          "text-[22px] leading-none font-semibold tracking-[-0.02em] tabular-nums",
          tone === "warn" && "text-amber-600 dark:text-amber-400",
        )}
      >
        {value}
      </dd>
      {detail !== undefined && (
        <dd
          className={cn(
            "truncate text-xs text-muted-foreground",
            tone === "good" && "text-emerald-600 dark:text-emerald-400",
            tone === "bad" && "text-red-600 dark:text-red-400",
          )}
        >
          {detail}
        </dd>
      )}
    </div>
  );
}
